/**
 * Auth0-style "Getting Started" surface.
 *
 * The landing experience for a fresh tenant: a hero with overall progress, a
 * prominent "Start building" call-to-action, and a "Next steps" grid of the
 * onboarding tasks (connect a wallet, invite the team, upload ABIs, build the
 * catalog, create a pipeline). Completion is inferred by
 * {@link useOnboardingProgress}; each card deep-links to the real feature page
 * and flips to a "Done" state once its signal is satisfied.
 *
 * Presentational only — it receives the resolved progress so both the
 * conditional Overview and the dedicated `/getting-started` route can render it.
 */
import * as React from "react";
import { Link } from "@tanstack/react-router";
import {
  ArrowRight,
  CheckCircle2,
  HelpCircle,
  Circle,
  FileCode2,
  Network,
  Rocket,
  WalletCards,
  Workflow,
  Users,
  type LucideIcon,
} from "lucide-react";
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Progress,
  cn,
} from "@authe/ui-core";
import { ROUTES } from "@/config";
import type { OnboardingProgress, OnboardingStepId } from "./useOnboardingProgress";

const STEP_ICONS: Record<OnboardingStepId, LucideIcon> = {
  wallet: WalletCards,
  team: Users,
  contracts: FileCode2,
  catalog: Network,
  pipeline: Workflow,
};

export interface GettingStartedProps {
  progress: OnboardingProgress;
  /** Optional "skip / dismiss" affordance (hidden when omitted). */
  onDismiss?: () => void;
}

export function GettingStarted({ progress, onDismiss }: GettingStartedProps): React.ReactElement {
  const { completedCount, total } = progress;
  // Only the steps this reader can actually take. A card for a step they have
  // no scope for is a to-do that cannot be done, and its CTA lands on a
  // permission wall.
  const steps = progress.steps.filter((step) => !step.unavailable);
  // The cards include the OPTIONAL one; `total` counts only the required. Said
  // plainly, because leaving it unsaid produced "4 of 4 complete" above a
  // visibly unticked "Connect a wallet" card — a progress line and a card grid
  // disagreeing about what they are counting, with nothing to tell the reader
  // which to believe.
  const optional = steps.filter((step) => step.optional === true).length;
  const pct = total > 0 ? Math.round((completedCount / total) * 100) : 0;
  const allRequiredDone = completedCount === total && total > 0;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="max-w-2xl">
          <h1 className="text-2xl font-semibold tracking-tight text-foreground">Getting started</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            New to authe? Follow this guide to set up your workspace and start tracing on-chain
            activity in minutes.
          </p>
        </div>
        {onDismiss && (
          <Button type="button" variant="ghost" size="sm" onClick={onDismiss}>
            Skip for now
          </Button>
        )}
      </div>

      {/* Overall progress */}
      <Card>
        <CardContent className="flex flex-wrap items-center gap-4 pt-6">
          <div className="min-w-[200px] flex-1">
            <div className="mb-1.5 flex items-center justify-between text-sm">
              <span className="font-medium text-foreground">Your progress</span>
              <span className="tabular-nums text-muted-foreground">
                {completedCount} of {total} complete
                {optional > 0 && (
                  <span className="ml-1 tracking-normal">
                    {`· ${optional} optional`}
                  </span>
                )}
              </span>
            </div>
            <Progress value={pct} />
          </div>
          {allRequiredDone && (
            <Badge variant="secondary" className="gap-1">
              <CheckCircle2 className="h-3.5 w-3.5" />
              {/* Not "All set" when an offered card is still unticked — the
                  reader can see it, and a badge that contradicts the grid is
                  read as the badge being wrong. */}
              {optional > 0 ? "Setup complete" : "All set"}
            </Badge>
          )}
        </CardContent>
      </Card>

      {/* Start building hero */}
      <Card className="border-primary/30 bg-primary/5">
        <CardHeader className="flex-row items-start gap-4">
          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
            <Rocket className="h-5 w-5" />
          </div>
          <div className="min-w-0">
            <CardTitle>Start building</CardTitle>
            <CardDescription>
              Explore cross-chain transactions across every tenancy scope, or jump into the latest
              blocks live. This is the core surface the rest of your setup feeds into.
            </CardDescription>
          </div>
        </CardHeader>
        <CardContent className="flex flex-wrap gap-2">
          <Button asChild>
            <Link to={ROUTES.transactions}>
              Explore transactions
              <ArrowRight className="h-4 w-4" />
            </Link>
          </Button>
          <Button asChild variant="outline">
            <Link to={ROUTES.wallet}>
              <WalletCards className="h-4 w-4" />
              Connect a wallet
            </Link>
          </Button>
        </CardContent>
      </Card>

      {/* Next steps */}
      <div>
        <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-muted-foreground">
          Next steps
        </h2>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {steps.map((step) => (
            <StepCard
              key={step.id}
              icon={STEP_ICONS[step.id]}
              title={step.title}
              description={step.description}
              to={step.to}
              cta={step.cta}
              done={step.done}
              loading={step.loading}
              failed={step.failed === true}
              optional={step.optional === true}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

interface StepCardProps {
  icon: LucideIcon;
  title: string;
  description: string;
  to: string;
  cta: string;
  done: boolean;
  loading: boolean;
  /** Its probe errored — we cannot say whether this is done. */
  failed: boolean;
  /** Offered, not owed: it does not count towards completion. */
  optional: boolean;
}

function StepCard({
  icon: Icon,
  title,
  description,
  to,
  cta,
  done,
  loading,
  failed,
  optional,
}: StepCardProps): React.ReactElement {
  return (
    <Card className={cn("flex flex-col transition-colors", done && "border-emerald-500/40 bg-emerald-500/5")}>
      <CardHeader className="flex-row items-start justify-between gap-2">
        <div className="flex h-9 w-9 items-center justify-center rounded-md bg-muted text-foreground">
          <Icon className="h-5 w-5" />
        </div>
        {done ? (
          <CheckCircle2 className="h-5 w-5 text-emerald-600 dark:text-emerald-400" aria-label="Done" />
        ) : failed ? (
          // Not an empty circle: an empty circle is a claim that this is still
          // to do, and the read that would have told us failed.
          <HelpCircle
            className="h-5 w-5 text-muted-foreground"
            aria-label="Couldn't check this step"
          />
        ) : (
          <Circle className={cn("h-5 w-5 text-muted-foreground/40", loading && "animate-pulse")} aria-hidden />
        )}
      </CardHeader>
      <CardContent className="flex flex-1 flex-col">
        <CardTitle className="text-base">
          {title}
          {optional && (
            <span className="ml-2 align-middle text-xs font-normal text-muted-foreground">
              Optional
            </span>
          )}
        </CardTitle>
        <CardDescription className="mt-1 flex-1">
          {failed ? "We couldn't check this one — the read didn't come back." : description}
        </CardDescription>
        <div className="mt-4">
          <Button asChild variant={done ? "ghost" : "secondary"} size="sm">
            <Link to={to}>
              {done ? "Review" : cta}
              <ArrowRight className="h-4 w-4" />
            </Link>
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
